import {Injectable, OnDestroy} from "@angular/core";
import {HttpClient, HttpParams} from "@angular/common/http";
import {catchError} from "rxjs/operators";
import {Subject, throwError, timer} from "rxjs";
import {switchMap, tap, share, retry, takeUntil} from 'rxjs/operators';
import {NavigationConfiguration} from "../../models/navigation-configuration";
import {NavigationConfigurationMenuitem} from "../../models/navigation-configuration-menuitem";
import {HelperService} from "../helperService";
import {HalHelper} from '../../helpers/hal.helper';

@Injectable()
export class NavigationService implements OnDestroy
{

	/**
	 * Attribute
	 */
    private _OBJ_navigation_config: NavigationConfiguration;
	private OBJ_stop_polling = new Subject();


	/**
	 * Function > constructor
	 * @param {HttpClient} OBJ_http
	 * @param {HalHelper} OBJ_hal_helper
	 * @param {HelperService} OBJ_helper_service
	 */
	constructor(
		private OBJ_http: HttpClient,
		private OBJ_hal_helper: HalHelper,
		private OBJ_helper_service: HelperService
    ) {} // end function constructor

	ngOnDestroy() {
		this.OBJ_stop_polling.next();
	}


	/**
	 * Function > getSubmenuItemsByRoute
	 * @desc Gibt das Navigationsitem zur uebergebenen Route zurueck
	 * @param {string} STR_route
	 * @param OBJ_menu_items
	 * @return any
	 */
	public getSubmenuItemsByRoute(STR_route: string, OBJ_menu_items = this.OBJ_navigation_config): NavigationConfigurationMenuitem
	{
		return this.OBJ_hal_helper.getObjectByProperty("route", STR_route, OBJ_menu_items.menuitems);
	} // end function getSubmenuItemsByRoute


    /**
	 * Function > getDataEndpointByRoute
	 *
	 * @desc Liefert URL zum Daten-RESTEndpoint fuer Listing-Inhalt
	 * @param STR_route_menu
	 * @param {string} STR_route_submenu
	 * @param BOO_new
	 * @param STR_listing_id
	 */
	public getDataEndpointByRoute(STR_route_menu: string, STR_route_submenu: string, STR_listing_id = "", BOO_new = false): string
	{
		// Zuerst korrekten Menuepunkt holen
		let ARR_menuitems = this.getSubmenuItemsByRoute(STR_route_menu);

		let STR_route = "";

		if(STR_listing_id)
		{
			STR_route = this.getSubmenuItemsByRoute(STR_route_submenu, ARR_menuitems).nested_listing._links.values.href;
			// Ersetze die ID des uebergeordneten Wertes
			STR_route = STR_route.replace(":id", STR_listing_id);
		}
		else
		{
			let OBJ_submenuitems = this.getSubmenuItemsByRoute(STR_route_submenu, ARR_menuitems);

			// Nicht alle Menuepunkte haben Dataendpoints (z.B. reine iFrame-Anbindungen nicht)
			if(OBJ_submenuitems.hasOwnProperty("_links") && OBJ_submenuitems._links.hasOwnProperty("values"))
			{
				STR_route = this.getSubmenuItemsByRoute(STR_route_submenu, ARR_menuitems)._links.values.href;
			} // end if

		} // end if

        STR_route += BOO_new?'/new':'';

        // console log
        this.OBJ_helper_service.log
        (
            {class: 'NavigationService', function: 'getDataEndpointByRoute'},
            'STR_route_menu: ' + STR_route_menu,
            'STR_route_submenu: ' + STR_route_submenu,
            'STR_route: ' + STR_route
        );

		return STR_route;
	} // end function getDataEndpointByRoute


	/**
	 * Function > getDataEndpointByRoute
	 *
	 * @desc Liefert Params zum Daten-RESTEndpoint fuer Listing-Inhalt
	 * @param STR_route_menu
	 * @param {string} STR_route_submenu
	 */
	public getDataEndpointParamsByRoute(STR_route_menu: string, STR_route_submenu: string): HttpParams
	{
		// Zuerst korrekten Menuepunkt holen
		let ARR_menuitems = this.getSubmenuItemsByRoute(STR_route_menu);

		let OBJ_value_endpoint = this.getSubmenuItemsByRoute(STR_route_submenu, ARR_menuitems)._links.values;

		let OBJ_http_params = new HttpParams();
		// Sind params gesetzt?
		if(this.OBJ_hal_helper.getProperty("params", OBJ_value_endpoint)!= null)
		{
			for (let OBJ_param of this.OBJ_hal_helper.getProperty("params", OBJ_value_endpoint))
			{
				OBJ_http_params = OBJ_http_params.set(OBJ_param.name, OBJ_param.value);
			} // end for

		} // end if

		// console log
		this.OBJ_helper_service.log
		(
			{class: 'NavigationService', function: 'getDataEndpointParamsByRoute'},
			'OBJ_http_params: ' + OBJ_http_params,
		);

		return OBJ_http_params;
	} // end function getDataEndpointParamsByRoute


	/**
	 * Function > getListingConfigEndpointByRoute
	 *
	 * @desc Liefert URL zum REST-Endpoint fuer Listing-Konfiguration
	 * @param STR_route_menu
	 * @param {string} STR_route_submenu
	 * @param STR_listing_id
	 */
	public getListingConfigEndpointByRoute(STR_route_menu: string, STR_route_submenu: string, STR_listing_id = ""): string
	{

        // console log
        this.OBJ_helper_service.log
        (
            {class: 'NavigationService', function: 'getListingConfigEndpointByRoute'},
            'STR_route_menu: ' + STR_route_menu,
            'STR_route_submenu: ' + STR_route_submenu
        );

		// Zuerst korrekten Menuepunkt holen
		let ARR_menuitems = this.getSubmenuItemsByRoute(STR_route_menu);

		let OBJ_submenuitem = this.getSubmenuItemsByRoute(STR_route_submenu, ARR_menuitems);

		if(STR_listing_id)
		{
			return OBJ_submenuitem.nested_listing._links.listingconfig.href;
		}

		// Jetzt Link aus Untermenuepunkt auslesen
		return OBJ_submenuitem._links.listingconfig.href;
	} // end function getListingConfigEndpointByRoute


	/**
	 * Function > getBulkinsertConfigEndpointByRoute
	 *
	 * @desc Liefert URL zum REST-Endpoint Bulkinsert Listing-Konfiguration oder null wenn nicht gesetzt
	 * @param STR_route_menu
	 * @param {string} STR_route_submenu
	 */
	public getBulkinsertConfigEndpointByRoute(STR_route_menu: string, STR_route_submenu: string): string
	{

		// console log
		this.OBJ_helper_service.log
		(
			{class: 'NavigationService', function: 'getBulkinsertConfigEndpointByRoute'},
			'STR_route_menu: ' + STR_route_menu,
			'STR_route_submenu: ' + STR_route_submenu
		);

		// Zuerst korrekten Menuepunkt holen
		let ARR_menuitems = this.getSubmenuItemsByRoute(STR_route_menu);

		let OBJ_submenuitem = this.getSubmenuItemsByRoute(STR_route_submenu, ARR_menuitems);

		if(OBJ_submenuitem && OBJ_submenuitem._links.bulkinsertconfig)
		{
			// Jetzt Link aus Untermenuepunkt auslesen
			return OBJ_submenuitem._links.bulkinsertconfig.href;
		}

		return null;

	} // end function getBulkinsertConfigEndpointByRoute


	/**
	 * Function > hasBulkinsert
	 *
	 * @param STR_route_menu
	 * @param {string} STR_route_submenu
	 */
	public hasBulkinsert(STR_route_menu: string, STR_route_submenu: string): boolean
	{

		// console log
		this.OBJ_helper_service.log
		(
			{class: 'NavigationService', function: 'hasBulkinsert'},
			'STR_route_menu: ' + STR_route_menu,
			'STR_route_submenu: ' + STR_route_submenu
		);

		return this.getBulkinsertConfigEndpointByRoute(STR_route_menu, STR_route_submenu) !== null;

	} // end function hasBulkinsert

	/**
	 * Function > getBulkinsertConfigEndpointByRoute
	 *
	 * @desc Liefert URL zum REST-Endpoint Bulkinsert Listing-Konfiguration oder null wenn nicht gesetzt
	 * @param STR_route_menu
	 * @param {string} STR_route_submenu
	 */
	public isReducedDetail(STR_route_menu: string, STR_route_submenu: string): boolean
	{

		// Zuerst korrekten Menuepunkt holen
		let ARR_menuitems = this.getSubmenuItemsByRoute(STR_route_menu);

		let OBJ_submenuitem = this.getSubmenuItemsByRoute(STR_route_submenu, ARR_menuitems);

		if(OBJ_submenuitem && OBJ_submenuitem._links.detailconfig && OBJ_submenuitem._links.detailconfig.type)
		{
			return OBJ_submenuitem._links.detailconfig.type === "reduceddetail";
		}

		return false;

	} // end function getBulkinsertConfigEndpointByRoute


	/**
	 * Function > getMenuitemConfigByRoute
	 *
	 * @desc Liefert Konfiguration eines Untermenuepunktes
	 * @param STR_route_menu
	 * @param {string} STR_route_submenu
	 * @param STR_listing_id
	 */
	public getMenuitemConfigByRoute(STR_route_menu: string, STR_route_submenu: string, STR_listing_id =""): NavigationConfigurationMenuitem
	{

		// console log
		this.OBJ_helper_service.log
		(
			{class: 'NavigationService', function: 'getMenuitemConfigByRoute'},
			'STR_route_menu: ' + STR_route_menu,
			'STR_route_submenu: ' + STR_route_submenu
		);

		// Zuerst korrekten Menuepunkt holen
		let ARR_menuitems = this.getSubmenuItemsByRoute(STR_route_menu);

		// Jetzt Link aus Untermenuepunkt auslesen
		let OBJ_submenuitems = this.getSubmenuItemsByRoute(STR_route_submenu, ARR_menuitems);

		if(STR_listing_id)
		{
			OBJ_submenuitems = this.OBJ_hal_helper.getProperty("nested_listing", OBJ_submenuitems);
		}

		return OBJ_submenuitems;
	} // end function getMenuitemConfigByRoute

	/**
	 * Function > getDetailConfigEndpoint
	 *
	 * @desc Liefert URL zum REST-Endpoint fuer Detail-Config
	 * @param route_menu
	 * @param route_submenu
	 * @param listing_id
	 * @param config
	 */
	public getDetailConfigEndpointByRoute(route_menu: string, route_submenu: string, listing_id: string = "",  config: string = ""): string
	{

        // console log
        this.OBJ_helper_service.log
        (
            {class: 'NavigationService', function: 'getDetailConfigEndpointByRoute'},
            'STR_route_menu: ' + route_menu,
            'STR_route_submenu: ' + route_submenu,
			'config: ' + config
        );

		// Zuerst korrekten Menuepunkt holen
		let menuitems = this.getSubmenuItemsByRoute(route_menu);

		let submenuitem = this.getSubmenuItemsByRoute(route_submenu, menuitems);

		// Jetzt Link aus Untermenuepunkt auslesen
		if(listing_id)
		{
			return submenuitem.nested_listing._links.detailconfig.href;
		}
		else
		{
			// Pruefe ob es unterschiedliche Detailconfigs gibt und waehle die gewuenschte
			if(submenuitem._links.detailconfig.configs && config)
			{
				for (let item of submenuitem._links.detailconfig.configs) {
					if(item.param === config)
					{
						return item.value;
					}
				}

			}

			return submenuitem._links.detailconfig.href;
		} // end if

	} // end function getDetailConfigEndpoint


	/**
	 * Function > getDetailConfigRoute
	 *
	 * @desc Liefert URL zum REST-Endpoint fuer Detail-Config
	 * @param route_menu
	 * @param route_submenu
	 * @param classname
	 */
	public getDetailconfigRouteByClassname(route_menu: string, route_submenu: string, classname: string): string
	{

		// console log
		this.OBJ_helper_service.log
		(
			{class: 'NavigationService', function: 'getDetailConfigEndpointByRoute'},
			'STR_route_menu: ' + route_menu,
			'STR_route_submenu: ' + route_submenu,
			'classname: ' + classname
		);

		// Zuerst korrekten Menuepunkt holen
		let menuitems = this.getSubmenuItemsByRoute(route_menu);

		let submenuitem = this.getSubmenuItemsByRoute(route_submenu, menuitems);

		// Pruefe ob es unterschiedliche Detailconfigs gibt und waehle die gewuenschte
		if(submenuitem._links.detailconfig.configs && classname)
		{
			for (let item of submenuitem._links.detailconfig.configs) {
				if(item.name === classname)
				{
					return item.param;
				}
			}

		}

		return null;

	} // end function getDetailConfigEndpoint


    /**
     * Function > load
     * @param {number} INT_client_id
	 * @param {number} INT_role_id
     * @desc This method loads "navigation.json" to get all navigation variables
     */
    public load(INT_client_id: number, INT_role_id: number)
    {

    	// navigation path
        let STR_nav_url_path;

		// get current user
		let OBJ_current_user = this.OBJ_helper_service.getFromLocalstorage('current_user');

		// get navigation path for specific client an role
		OBJ_current_user.clients.forEach((OBJ_client) => {
			if (OBJ_client.id == INT_client_id) {
				OBJ_client.roles.forEach((OBJ_role) => {
					if (OBJ_role.id == INT_role_id) {
						STR_nav_url_path = OBJ_role.navigation.url;
					} // end if
				});
			} // end if
		});

        return new Promise((resolve) =>
        {

			// set navigation path
			this.OBJ_helper_service.setInLocalstorage('navigation_path', {'path': STR_nav_url_path});

			// console log
			this.OBJ_helper_service.log
			(
				{class: 'NavigationService', function: 'load'},
				"trying to get navigation from path: " + STR_nav_url_path
			);

            // remove navigation from local storage
			this.OBJ_helper_service.removeFromStorage('navigation');

			// load navi periodically (all 10 minutes --> 600000 milliseconds)
			this.loadPeriodically(STR_nav_url_path, 600000, resolve);

        });

    } // end function load


    /**
     * Getter > OBJ_navigation_config
     * @return {any}
     * @constructor
     */
    get OBJ_navigation_config(): any
    {

    	// Wenn Navigation null oder undefined ist, lade sie aus cache
        if (!this._OBJ_navigation_config) {

            // get navigation
            this.OBJ_navigation_config = this.OBJ_helper_service.getFromLocalstorage('navigation');

            // console log
            this.OBJ_helper_service.log
			(
				{class: 'NavigationService', function: 'get'},
				"Loaded navigation config from cache...",
				this.OBJ_navigation_config
			);

        } // end if

        return this._OBJ_navigation_config;

    } // end getter OBJ_navigation_config


    /**
     * Setter > OBJ_navigation_config
     * @param OBJ_value
     * @constructor
     */
    set OBJ_navigation_config(OBJ_value: any)
    {
        this._OBJ_navigation_config = OBJ_value;
    } // end setter OBJ_navigation_config


	/**
	 * Function > loadPeriodically
	 *
	 * @param {string} STR_nav_url_path
	 * @param {number} INT_period_scheduler
	 * @param {resolve: (value?: unknown) => void} resolve
	 */
	public loadPeriodically(STR_nav_url_path: string, INT_period_scheduler: number = 600000, resolve: (value?: unknown) => void = null): void
	{

		/**
		 * @desc "dueTime" and "periodScheduler" in milliseconds. 600000 milliseconds -> 10 minutes.
		 * @see https://blog.angulartraining.com/how-to-do-polling-with-rxjs-and-angular-50d635574965
		 * */
		timer(1, INT_period_scheduler).pipe(
			switchMap(() => this.OBJ_http.get(STR_nav_url_path)
				.pipe(
					catchError((OBJ_error: any): any =>
					{
						// resolve(true);
						return throwError("Navigation-file could not be loaded");
					})
				)
			)
			,
			retry(),
			share(),
			takeUntil(this.OBJ_stop_polling)
		)
		.subscribe((OBJ_response) =>
		{

			// console log
			let OBJ_date = new Date();
			this.OBJ_helper_service.log
			(
				{class: 'NavigationService', function: 'loadPeriodically'},
				"Loading navigation config from backend..." + OBJ_date.toString(),
				OBJ_response
			);

			this.OBJ_navigation_config = OBJ_response;

			// set navigation
			this.OBJ_helper_service.setInLocalstorage('navigation', OBJ_response);

			// call the resolve() function
			if (resolve !== null) {
				resolve(true);
			} // end if

		});

	} // end function loadPeriodically


} // end class NavigationService
